import { useSelector } from 'react-redux';
import { getRegion } from 'shared/store/selectors/user.selector';
import { isDocumentEditorEnabled } from 'shared/store/selectors/features.selector';
import { REGIONS } from 'shared/constants/region.constants';
import { KNOWLEDGE_TYPES } from 'shared/constants';
import { isSpreadsheetFile, isDocumentEditorFileType } from 'shared/utils/Utils';

/** Whether the Document Editor feature is available for the current user (feature flag + region) */
export function useIsDocumentEditorSupported() {
  const isFeatureEnabled = useSelector(isDocumentEditorEnabled);
  const userRegion = useSelector(getRegion);
  return isFeatureEnabled && userRegion === REGIONS.US;
}

/**
 * Hook to check if the document editor should be used for a given knowledge item.
 * Handles spreadsheets (can render in editor), documents, and presentations (show fallback).
 *
 * External links are never routed through the document editor — even if their URL ends
 * in a supported extension (e.g. a SharePoint .xlsx share link), the host page may block
 * iframe embedding and the file is not under our control.
 *
 * @param {string | undefined | null} filename - The filename or URL to check
 * @param {string | undefined | null} type - The knowledge item type (KNOWLEDGE_TYPES)
 * @returns {{
 *   isSupported: boolean,
 *   canRenderInEditor: boolean,
 *   shouldUseDocumentEditor: boolean
 * }}
 */
export function useDocumentEditor(filename, type) {
  const isExternalLink = type === KNOWLEDGE_TYPES.EXTERNAL_LINK;
  const isSpreadsheet = !isExternalLink && isSpreadsheetFile(filename);
  const isDocEditorFileType = !isExternalLink && isDocumentEditorFileType(filename);

  // Feature support (region + flag) — independent of the item type
  const isSupported = useIsDocumentEditorSupported();

  // Can render in editor: must be a spreadsheet uploaded file AND feature supported
  const canRenderInEditor = isSpreadsheet && isSupported;

  // Should use DocumentEditor component for this file type (spreadsheets, docs, presentations)
  const shouldUseDocumentEditor = isDocEditorFileType;

  return {
    isSupported,
    canRenderInEditor,
    shouldUseDocumentEditor,
  };
}
